import { Component, ElementRef, Input, OnInit } from '@angular/core';
import { PopupCloseEvent, SinglePopupService } from '@progress/kendo-angular-grid';
import { addDays } from '@progress/kendo-date-math';
import { FilterBase } from './filter-base';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateInputsModule, PopupSettings } from '@progress/kendo-angular-dateinputs';
import { FormsModule } from '@angular/forms';

const closest = (node: any, predicate: any): any => {
    while (node && !predicate(node)) {
        node = node.parentNode;
    }
    return node;
};

@UntilDestroy()
@Component({
    selector: 'nova-date-range-filter',
    template: ` <div class="k-form">
        <label class="k-form-field">
            <span>After</span>
            <kendo-datepicker
                (valueChange)="onStartChange($event)"
                format="MM/dd/yy"
                [(ngModel)]="start"
                [max]="max"
                [popupSettings]="popupSettings">
            </kendo-datepicker>
        </label>
        <label class="k-form-field">
            <span>Before</span>
            <kendo-datepicker
                (valueChange)="onEndChange($event)"
                format="MM/dd/yy"
                [(ngModel)]="end"
                [min]="min"
                [popupSettings]="popupSettings">
            </kendo-datepicker>
        </label>
    </div>`,
    styles: [
        `
            .k-form {
                padding: 5px;
            }
        `
    ],
    standalone: true,
    imports: [FormsModule, DateInputsModule]
})
export class DateRangeFilterComponent extends FilterBase implements OnInit {
    @Input() uniqueFilterClass = '';
    @Input() allowSameDaySelection = false;

    start: Date;
    end: Date;

    public popupSettings: PopupSettings = {
        popupClass: 'date-range-filter'
    };

    constructor(
        private readonly _element: ElementRef,
        private readonly _popupService: SinglePopupService
    ) {
        super();
    }

    get min(): Date | null {
        return this.start ? addDays(this.start, this.allowSameDaySelection ? 0 : -2) : null;
    }

    get max(): Date | null {
        return this.end ? addDays(this.end, this.allowSameDaySelection ? 0 : -1) : null;
    }

    ngOnInit(): void {
        // Handle the service onClose event and prevent the menu from closing when the datepickers are still active.
        this._popupService.onClose.pipe(untilDestroyed(this)).subscribe((e: PopupCloseEvent) => {
            if (
                document.activeElement &&
                closest(
                    document.activeElement,
                    (node) =>
                        node === this._element.nativeElement || String(node.className).includes('date-range-filter')
                )
            ) {
                e.preventDefault();
            }
        });

        this.start = this.getLatestFilterValue('gte');
        this.end = this.getLatestFilterValue('lte');
    }

    onStartChange(value: Date): void {
        this.filterRange(value, this.end);
    }

    onEndChange(value: Date): void {
        this.filterRange(this.start, value);
    }

    private filterRange(start: Date, end: Date): void {
        const filters = [];

        if (this.allowSameDaySelection) {
            if (start && (!end || start <= end)) {
                filters.push({
                    field: this.field,
                    operator: 'gte',
                    value: start
                });
                this.start = start;
            }

            if (end && (!start || start <= end)) {
                filters.push({
                    field: this.field,
                    operator: 'lte',
                    value: end
                });
                this.end = end;
            }
        } else {
            if (start && (!end || start < end)) {
                filters.push({
                    field: this.field,
                    operator: 'gte',
                    value: start
                });
                this.start = start;
            }

            if (end && (!start || start < end)) {
                filters.push({
                    field: this.field,
                    operator: 'lte',
                    value: end
                });
                this.end = end;
            }
        }

        this.filterService.filter({
            logic: 'and',
            filters
        });
    }
}
